import { FC, useEffect, useRef, useState } from "react";
import { useAudio } from "../contexts/AudioContext";

type Props = {
  audioUrl: string;
  className?: string;
};

const formatTime = (seconds: number): string => {
  if (isNaN(seconds)) return "0:00";
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
};

export const SimpleAudioProgress: FC<Props> = ({
  audioUrl,
  className = "",
}) => {
  const { currentPlayingRef, currentTime, duration, seekTo } = useAudio();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [localDuration, setLocalDuration] = useState(0);
  const [localCurrentTime, setLocalCurrentTime] = useState(0);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    let mounted = true;

    const findAndSetupAudio = async () => {
      // Attendre un peu pour que les éléments audio soient créés
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Méthodes de recherche plus robustes
      const findAudioElement = () => {
        // Méthode 1: par src exact
        let audioElement = document.querySelector(
          `audio[src="${audioUrl}"]`
        ) as HTMLAudioElement;

        if (!audioElement) {
          // Méthode 2: chercher dans toutes les sources
          const allAudios = document.querySelectorAll("audio");
          for (const audio of allAudios) {
            const source = audio.querySelector("source");
            if (source && source.src === audioUrl) {
              audioElement = audio;
              break;
            }
            if (audio.src === audioUrl) {
              audioElement = audio;
              break;
            }
            // Vérifier aussi si l'audio a un src qui correspond
            if (audio.currentSrc === audioUrl) {
              audioElement = audio;
              break;
            }
          }
        }

        if (!audioElement) {
          // Méthode 3: par nom de fichier
          const allAudios = document.querySelectorAll("audio");
          const urlFileName = audioUrl.split("/").pop();
          if (urlFileName) {
            for (const audio of allAudios) {
              if (
                audio.src.includes(urlFileName) ||
                audio.currentSrc.includes(urlFileName)
              ) {
                audioElement = audio;
                break;
              }
              const source = audio.querySelector("source");
              if (source && source.src.includes(urlFileName)) {
                audioElement = audio;
                break;
              }
            }
          }
        }

        console.log("Audio search result:", {
          audioUrl,
          urlFileName: audioUrl.split("/").pop(),
          found: !!audioElement,
          elementSrc: audioElement?.src,
          elementCurrentSrc: audioElement?.currentSrc,
          elementDuration: audioElement?.duration,
          readyState: audioElement?.readyState,
          networkState: audioElement?.networkState,
          allAudiosCount: document.querySelectorAll("audio").length,
        });

        return audioElement;
      };
      const setupAudioElement = (audioElement: HTMLAudioElement) => {
        if (!mounted) return;

        audioRef.current = audioElement;

        const handleLoadedMetadata = () => {
          console.log("Metadata loaded:", audioElement.duration);
          if (mounted) {
            setLocalDuration(audioElement.duration || 0);
            setIsReady(true);
          }
        };

        const handleTimeUpdate = () => {
          if (mounted) {
            setLocalCurrentTime(audioElement.currentTime || 0);
          }
        };

        const handleLoadedData = () => {
          console.log("Data loaded:", audioElement.duration);
          if (mounted && audioElement.duration) {
            setLocalDuration(audioElement.duration);
            setIsReady(true);
          }
        };

        // Si déjà chargé
        if (audioElement.duration) {
          setLocalDuration(audioElement.duration);
          setIsReady(true);
        }

        // Forcer le chargement des métadonnées selon l'état
        if (audioElement.readyState === 0 || audioElement.networkState === 0) {
          console.log("Loading audio metadata...", {
            readyState: audioElement.readyState,
            networkState: audioElement.networkState,
            src: audioElement.src || audioElement.currentSrc,
          });
          audioElement.load();
        }

        // Si l'audio n'a pas d'attribut preload, l'ajouter
        if (!audioElement.hasAttribute("preload")) {
          audioElement.preload = "metadata";
        }

        // Ajouter les listeners
        audioElement.addEventListener("loadedmetadata", handleLoadedMetadata);
        audioElement.addEventListener("loadeddata", handleLoadedData);
        audioElement.addEventListener("timeupdate", handleTimeUpdate);

        return () => {
          audioElement.removeEventListener(
            "loadedmetadata",
            handleLoadedMetadata
          );
          audioElement.removeEventListener("loadeddata", handleLoadedData);
          audioElement.removeEventListener("timeupdate", handleTimeUpdate);
        };
      };

      // Essayer de trouver l'élément plusieurs fois
      for (let attempt = 0; attempt < 20; attempt++) {
        if (!mounted) break;

        const audioElement = findAudioElement();
        if (audioElement) {
          const cleanup = setupAudioElement(audioElement);
          return cleanup;
        }

        // Attendre avant le prochain essai
        await new Promise((resolve) => setTimeout(resolve, 250));
      }

      console.warn("Could not find audio element for URL:", audioUrl);
    };

    const cleanup = findAndSetupAudio();

    return () => {
      mounted = false;
      if (cleanup) {
        cleanup.then((fn) => fn && fn());
      }
    };
  }, [audioUrl]);

  // Déterminer les valeurs à utiliser
  const isCurrentlyPlaying = currentPlayingRef?.current === audioRef.current;
  const actualDuration =
    isCurrentlyPlaying && duration > 0 ? duration : localDuration;
  const actualCurrentTime = isCurrentlyPlaying ? currentTime : localCurrentTime;

  const handleProgressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const clickPosition = parseFloat(e.target.value);
    const targetDuration = actualDuration || localDuration;

    if (!audioRef.current || targetDuration === 0) {
      console.log("Cannot seek: no audio element or duration", {
        hasAudio: !!audioRef.current,
        targetDuration,
        actualDuration,
        localDuration,
      });
      return;
    }

    const newTime = (clickPosition / 100) * targetDuration;
    console.log("Seeking to:", newTime, "out of", targetDuration);

    if (isCurrentlyPlaying && seekTo) {
      // Si c'est l'audio en cours, utiliser le contexte
      seekTo(newTime);
    } else if (audioRef.current) {
      // Sinon, modifier directement l'élément
      try {
        audioRef.current.currentTime = newTime;
        setLocalCurrentTime(newTime);
        console.log("Seek successful, new time:", newTime);
      } catch (error) {
        console.error("Seek failed:", error);
      }
    }
  };

  const progress =
    actualDuration > 0 ? (actualCurrentTime / actualDuration) * 100 : 0;

  console.log("SimpleAudioProgress render:", {
    audioUrl: audioUrl.split("/").pop(),
    isCurrentlyPlaying,
    isReady,
    actualDuration,
    actualCurrentTime,
    progress,
    localDuration,
    localCurrentTime,
    contextDuration: duration,
    contextCurrentTime: currentTime,
    audioElementFound: !!audioRef.current,
    audioElementSrc: audioRef.current?.src,
    audioElementDuration: audioRef.current?.duration,
  });
  return (
    <div className={`space-y-2 ${className}`}>
      {/* Progress Bar */}
      <input
        type="range"
        min="0"
        max="100"
        value={progress}
        onChange={handleProgressChange}
        className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer slider"
        style={{
          background: `linear-gradient(to right, #5c93b0 0%, #5c93b0 ${progress}%, #e2e8f0 ${progress}%, #e2e8f0 100%)`,
        }}
      />

      {/* Time Display */}
      <div className="flex justify-between text-xs text-slate-500">
        <span>{formatTime(actualCurrentTime)}</span>
        <span>{formatTime(actualDuration)}</span>
      </div>

      <style>
        {`
          .slider::-webkit-slider-thumb {
            appearance: none;
            width: 12px;
            height: 12px;
            border-radius: 50%;
            background: #5c93b0;
            cursor: pointer;
            border: 2px solid white;
            box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
          }

          .slider::-moz-range-thumb {
            width: 12px;
            height: 12px;
            border-radius: 50%;
            background: #5c93b0;
            cursor: pointer;
            border: 2px solid white;
            box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
          }
        `}
      </style>
    </div>
  );
};
